// Use the country regex from the countryUtils file
import { countries, validatePostalCode, Country } from "./countryUtils";
import { calculateAge } from "./dateUtils";
import { parse } from "date-fns";

/**
 * Utility functions for form validation
 */

/**
 * Valid country code type
 */
export type ValidCountryCode = "US" | "CA"; // Note supported yet :)  | "AU" | "NZ";

/**
 * Type guard to check if a string is a valid country code
 */
export function isValidCountryCode(code?: string): code is ValidCountryCode {
  return countries.some((country) => country.code === code);
}

/**
 * Gets a country by its code, throws an error if the country is not found ~ configuration for the country is typed so this should never happen.
 * Majority of use cases will be as a type guard.
 */
export function getCountryByCode(code: string): Country {
  if (!isValidCountryCode(code)) {
    throw new Error(`Invalid country code: ${code}`);
  }

  // We can safely use non-null assertion here because the type guard ensures the country exists
  return countries.find((c) => c.code === code)!;
}

/**
 * Validates if a string is a valid US ZIP code (5 digits)
 */
export const isValidUSZipCode = (zipCode: string): boolean => {
  // Basic validation: 5 digits
  return /^\d{5}$/.test(zipCode);
};

/**
 * Returns the US state abbreviation for a given ZIP code using 3-digit prefix ranges.
 * Only covers states relevant to sensitive data consent requirements.
 */
export function getUSStateFromZipCode(zipCode: string): string | undefined {
  if (!zipCode || zipCode.length < 3) return undefined;
  const prefix = parseInt(zipCode.substring(0, 3), 10);
  if (isNaN(prefix)) return undefined;

  if (prefix >= 70 && prefix <= 89) return "NJ";
  if (prefix >= 206 && prefix <= 219) return "MD";
  if (prefix === 733 || (prefix >= 750 && prefix <= 799) || prefix === 885)
    return "TX";
  if (prefix >= 800 && prefix <= 816) return "CO";
  if (prefix >= 889 && prefix <= 898) return "NV";
  if (prefix >= 900 && prefix <= 961) return "CA";
  if (prefix >= 980 && prefix <= 994) return "WA";

  return undefined;
}

/**
 * Validates if a string is a valid Canadian postal code (format: A1A 1A1)
 */
export const isValidCanadianPostalCode = (postalCode: string): boolean => {
  // Canadian postal code format: A1A 1A1 or A1A1A1
  return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postalCode);
};

/**
 * Validates a postal code based on the country code
 */
export const isValidPostalCode = (
  postalCode: string,
  countryCode: string,
): boolean => {
  if (!isValidCountryCode(countryCode)) {
    return false;
  }

  const country = getCountryByCode(countryCode);
  return validatePostalCode(country, postalCode);
};

/**
 * Checks if a postal code is eligible for the service
 * In a real app, this would check against a database or API of valid service areas
 */
export const checkZipCodeEligibility = (
  postalCode: string,
  countryCode: string,
) => {
  // Validate country code first
  if (!isValidCountryCode(countryCode)) {
    return {
      isValid: false,
      message: `Invalid country code: ${countryCode}`,
    };
  }

  const country = getCountryByCode(countryCode);
  const isValid = validatePostalCode(country, postalCode);

  // Use country-specific terminology in the message
  const label = country.postalCodeLabel;
  const message = isValid ? `Valid ${label}` : `Invalid ${label} format`;

  return {
    isValid,
    message,
  };
};

/**
 * Checks if the date of birth indicates the person is under 18.
 * @param dateOfBirthString The date of birth string in MM/DD/YYYY format.
 * @returns True if under 18, false otherwise or if date is invalid.
 */
export function isUnderEighteen(dateOfBirthString: string): boolean {
  // Handle single digit month/day and future dates
  const dateOfBirth = parse(dateOfBirthString, "MM/dd/yyyy", new Date());
  const age = calculateAge(dateOfBirth);

  // Any age calculated from a future year will be negative
  // For future dates (2030+), they would definitely be under 18 now
  if (age !== null && (age < 18 || age < 0)) {
    return true;
  }

  return false;
}
